/**
 * The round "?" button in the app header and its slide-over help panel (ST-00200).
 *
 * Mounted once in the app shell (App.tsx ShellFrame), outside every router — the active path
 * arrives as a prop and links go through AppLink, so it works tabbed and untabbed. Sits in the
 * header bar next to the LexiStar notification bell (HelpLauncher.css).
 *
 *   click / Shift+?   open the panel with the guides for the page the user is on
 *   Esc               close it (focus returns to the button); Tab stays inside the panel
 *
 * The panel body (components/pages/Help/HelpPanel) and the Help content load lazily the first
 * time it opens. Hidden on the sign-in screens and when there is no signed-in user.
 */
import { lazy, Suspense, useCallback, useContext, useEffect, useId, useRef, useState, type Context, type CSSProperties, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import TabContextJs from '../../context/TabContext';
import { useAuth } from '../../types/commonComponents';
import { pageLabel } from '../../help/pageLabels';
import ErrorBoundary from './ErrorBoundary';
import './HelpLauncher.css';

const loadHelpPanel = () => import('../pages/Help/HelpPanel');
const HelpPanel = lazy(loadHelpPanel);
/** Start loading the panel (and the Help content) before the click: on hover or focus of "?". */
const prefetchPanel = (): void => {
  void loadHelpPanel().catch(() => {
    /* the click retries through React.lazy */
  });
};

const PUBLIC_PATHS = ['/login', '/unauthorized', '/forgot-password', '/set-password'];

// TabContext.js is untyped (createContext(null)); the launcher reads only the tab count.
const TabContext = TabContextJs as unknown as Context<{ tabs?: readonly unknown[] } | null>;

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

/** Typing in a field: '?' is a character there, not the shortcut. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

export interface HelpLauncherProps {
  activePathname: string;
}

export default function HelpLauncher({ activePathname }: HelpLauncherProps) {
  const { user, authEnabled } = useAuth();
  const [open, setOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  // Same place as the notification bell: below the tab strip when tabs are on.
  const tabs = useContext(TabContext);
  const desktopTop = tabs ? ((tabs.tabs?.length ?? 0) > 1 ? 34 : 28) + 2 : 2;
  const hidden = PUBLIC_PATHS.includes(activePathname) || (!!authEnabled && !user);

  const close = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  // Shift+? toggles the panel anywhere outside a text field; Esc closes it.
  useEffect(() => {
    if (hidden) return undefined;
    const onKey = (e: globalThis.KeyboardEvent): void => {
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e.target)) {
        e.preventDefault();
        setOpen((o) => !o);
        return;
      }
      if (e.key === 'Escape' && open && !e.defaultPrevented) {
        e.preventDefault();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [hidden, open, close]);

  // Focus the panel's close button when it opens (the search takes focus itself when the page
  // has no guide).
  useEffect(() => {
    if (open) closeRef.current?.focus();
  }, [open]);

  // Leaving the page closes the panel (a guide link was followed, or the user navigated).
  const lastPath = useRef(activePathname);
  useEffect(() => {
    if (lastPath.current !== activePathname) {
      lastPath.current = activePathname;
      setOpen(false);
    }
  }, [activePathname]);

  if (hidden) return null;

  const trapTab = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Tab' || !panelRef.current) return;
    const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
    );
    if (!items.length) return;
    const first = items[0] as HTMLElement;
    const last = items[items.length - 1] as HTMLElement;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="help-launcher"
        style={{ '--lx-help-top': `${desktopTop}px` } as CSSProperties}
        onClick={() => setOpen((o) => !o)}
        onPointerEnter={prefetchPanel}
        onFocus={prefetchPanel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Help for this page"
        aria-keyshortcuts="Shift+?"
        title="Help for this page (Shift + ?)"
        data-testid="help-launcher"
      >
        ?
      </button>
      {open &&
        createPortal(
          <>
            <div className="help-scrim" aria-hidden="true" onClick={close} />
            <div
              ref={panelRef}
              className="help-slideover"
              role="dialog"
              aria-modal="true"
              aria-labelledby={titleId}
              onKeyDown={trapTab}
            >
              <div className="help-slideover__head">
                <div style={{ minWidth: 0 }}>
                  <h2 id={titleId} className="help-slideover__title">
                    Help for this page
                  </h2>
                  <p className="help-slideover__sub">{pageLabel(activePathname)}</p>
                </div>
                <button ref={closeRef} type="button" className="help-slideover__close" onClick={close} aria-label="Close help">
                  <X size={20} aria-hidden="true" />
                </button>
              </div>
              {/* A failed help chunk (a tab open across a deploy) reloads once or shows the
                  panel here, instead of reaching the app-level boundary (ST-00215, PLT-3). */}
              <ErrorBoundary resetKey={activePathname}>
                <Suspense
                  fallback={
                    <div className="help-slideover__body">
                      <p role="status">Loading help…</p>
                    </div>
                  }
                >
                  <HelpPanel activePathname={activePathname} onClose={() => setOpen(false)} />
                </Suspense>
              </ErrorBoundary>
            </div>
          </>,
          document.body
        )}
    </>
  );
}
